import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AllTradesTable from "../../../Components/PerpsComponents/AllTradesTable";
import TotalPnL from "../../../Components/PerpsComponents/TotalPnL";
import { useWalletConnection } from "../../../hooks/useWalletConnection";
import CELogo from "../../../assets/images/CE-logo.jpg";
import { Tooltip } from "react-tooltip";

/**
 * All Trades Page
 * Displays all user trades across all market types in a unified view
 *
 * Surfaces follow the 2026 restyle: the page is the cool ground (`--bg`), and
 * everything on it is a card carrying `--card` rather than a white rectangle
 * with a hairline border. Nothing here authors a literal grey — the neutral
 * ramp lifts in dark mode and inside the Pokédex, a fixed hex does not.
 */
const AllTradesPage = () => {
  const { isConnected } = useWalletConnection();
  const account = useSelector((state) => state.auth.account);
  const navigate = useNavigate();
  const [openTrades, setOpenTrades] = useState([]);

  if (!isConnected || !account) {
    return (
      <div
        className="w-full min-h-screen flex items-center justify-center px-4 py-10"
        style={{ background: "var(--bg)" }}
      >
        <div
          className="text-center px-8 py-10 sm:px-12 sm:py-12 max-w-[420px] w-full"
          style={{
            background: "var(--surface)",
            borderRadius: "var(--r-card)",
            boxShadow: "var(--card)",
          }}
        >
          <img
            src={CELogo}
            alt="Coded Estate"
            className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-5 sm:mb-6"
            style={{ borderRadius: "var(--r-panel)", boxShadow: "var(--pillow-sm)" }}
          />
          <h2
            className="text-xl sm:text-2xl font-bold mb-2"
            style={{ color: "var(--ink)" }}
          >
            Connect Your Wallet
          </h2>
          <p
            className="text-sm sm:text-[15px] leading-relaxed"
            style={{ color: "var(--ink-3)" }}
          >
            Your positions, orders and trade history live here once a wallet is
            connected.
          </p>
          <button
            type="button"
            onClick={() => navigate("/perps/markets")}
            className="ce-btn ce-btn-surface w-full mt-6 text-sm"
            style={{ height: 46, borderRadius: "var(--r-inner)" }}
          >
            Browse markets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-[1400px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* ─── Page header ─── */}
        <div className="mb-4 sm:mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-xl sm:text-[26px] font-bold leading-tight"
              style={{ color: "var(--ink)" }}
            >
              My Trades
            </h1>
            <p className="text-xs sm:text-sm mt-1" style={{ color: "var(--ink-3)" }}>
              Positions, pending orders and history across every market
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/perps/markets")}
            className="ce-btn ce-btn-accent text-sm"
            style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            New trade
          </button>
        </div>

        {/* Portfolio Overview — always show when connected (has realized PnL even with no open trades) */}
        <TotalPnL openTrades={openTrades} />

        <AllTradesTable onOpenTradesChange={setOpenTrades} />
      </div>

      {/* Tooltips for portfolio overview — the panel is a floating surface, so
          it reads from the same tokens every other overlay does instead of
          pinning a near-black that inverts badly in dark mode. */}
      <Tooltip
        id="pnl-overview-tooltip"
        style={{
          borderRadius: "var(--r-inner)",
          padding: "8px 12px",
          zIndex: 999999,
          background: "var(--surface)",
          color: "var(--ink-2)",
          boxShadow: "var(--float)",
          fontSize: "12px",
          maxWidth: "280px",
          lineHeight: "1.4",
        }}
      />
    </div>
  );
};

export default AllTradesPage;
